import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  allCreatedSites,
  createNewSite,
  siteById,
  updateSite as updateSiteService,
  deleteSite,
  restoreSite as restoreSiteService,
  permanentlyDeleteSite,
  cloneSite as cloneSiteService,
  migrateSite as migrateSiteService,
  downloadCompleteSite as downloadCompleteSiteService,
  migrateEnvironment as migrateEnvironmentService,
  getPhpMyAdminLogin as getPhpMyAdminLoginService,
  getWpAdminLogin as getWpAdminLoginService,
  getQuickAccessLinks as getQuickAccessLinksService,
  getSiteMetrics as getSiteMetricsService,
} from "../../services/Site";

// Get all sites thunk
export const allSites = createAsyncThunk(
  "/sites/all",
  async (
    {
      environment = "",
      status = "",
      search = "",
      page = 1,
      limit = 10,
      includeDeleted = false,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await allCreatedSites({
        environment,
        status,
        search,
        page,
        limit,
        includeDeleted,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

// Create new site thunk
export const createSite = createAsyncThunk(
  "/sites/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createNewSite(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Get site by id thunk
export const fetchSiteById = createAsyncThunk(
  "/sites/fetch",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await siteById(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// update site thunk
export const updateSite = createAsyncThunk(
  "/sites/update",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await updateSiteService(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Soft delete site thunk
export const deleteSiteById = createAsyncThunk(
  "/sites/delete",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await deleteSite(siteId);
      return { ...response.data, siteId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Restore site thunk
export const restoreSite = createAsyncThunk(
  "/sites/restore",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await restoreSiteService(siteId);
      return { ...response.data, siteId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Permanently delete site thunk
export const permanentlyDeleteSiteById = createAsyncThunk(
  "/sites/permanentDelete",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await permanentlyDeleteSite(siteId);
      return { ...response.data, siteId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Clone site thunk
export const cloneSite = createAsyncThunk(
  "/sites/clone",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await cloneSiteService(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Migrate site thunk
export const migrateSite = createAsyncThunk(
  "/sites/migrate",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await migrateSiteService(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Download complete site thunk
export const downloadCompleteSite = createAsyncThunk(
  "/sites/downloadComplete",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await downloadCompleteSiteService(siteId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Download failed");
    }
  },
);

// Migrate environment thunk
export const migrateEnvironment = createAsyncThunk(
  "/sites/migrateEnvironment",
  async ({ siteId, payload }, { rejectWithValue }) => {
    try {
      const response = await migrateEnvironmentService(siteId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Migration failed",
      );
    }
  },
);

// Get PHPMyAdmin login thunk
export const getPhpMyAdminLogin = createAsyncThunk(
  "/sites/phpmyadminLogin",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await getPhpMyAdminLoginService(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data ||
          error.message ||
          "Failed to get PHPMyAdmin login",
      );
    }
  },
);

// Get WP Admin login thunk
export const getWpAdminLogin = createAsyncThunk(
  "/sites/wpAdminLogin",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await getWpAdminLoginService(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to get WP Admin login",
      );
    }
  },
);

// Get quick access links thunk
export const getQuickAccessLinks = createAsyncThunk(
  "/sites/quickAccess",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await getQuickAccessLinksService(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data ||
          error.message ||
          "Failed to get quick access links",
      );
    }
  },
);

// Get site metrics (storage, CPU, memory, performance)
export const fetchSiteMetrics = createAsyncThunk(
  "/sites/metrics",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await getSiteMetricsService(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to get site metrics",
      );
    }
  },
);

const siteSlice = createSlice({
  name: "site",
  initialState: {
    site: null,
    loading: false,
    error: null,
    newSite: null,
    singleSite: null,
    deleteLoading: false,
    deleteError: null,
    restoreLoading: false,
    restoreError: null,
    permanentDeleteLoading: false,
    permanentDeleteError: null,
    cloneLoading: false,
    cloneError: null,
    migrateLoading: false,
    migrateError: null,
    downloadLoading: false,
    downloadError: null,
    migrateEnvironmentLoading: false,
    migrateEnvironmentError: null,
    quickAccess: null,
    quickAccessLoading: false,
    quickAccessError: null,
    // Site metrics state
    siteMetrics: null,
    metricsLoading: false,
    metricsError: null,
  },
  reducers: {
    clearSingleSite: (state) => {
      state.singleSite = null;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearRestoreError: (state) => {
      state.restoreError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.deleteError = null;
      state.restoreError = null;
      state.permanentDeleteError = null;
      state.cloneError = null;
      state.migrateError = null;
      state.metricsError = null;
    },
    clearQuickAccess: (state) => {
      state.quickAccess = null;
      state.quickAccessError = null;
    },
    clearSiteMetrics: (state) => {
      state.siteMetrics = null;
      state.metricsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all sites
      .addCase(allSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(allSites.fulfilled, (state, action) => {
        state.loading = false;
        state.site = action.payload.data || action.payload;
      })
      .addCase(allSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Create site
      .addCase(createSite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSite.fulfilled, (state, action) => {
        state.loading = false;
        state.newSite = action.payload.data || action.payload;
      })
      .addCase(createSite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Fetch site by Id
      .addCase(fetchSiteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSiteById.fulfilled, (state, action) => {
        state.loading = false;
        state.singleSite = action.payload.data || action.payload;
      })
      .addCase(fetchSiteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Update site
      .addCase(updateSite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSite.fulfilled, (state, action) => {
        state.loading = false;
        state.singleSite = action.payload.data || action.payload;
      })
      .addCase(updateSite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Soft delete site
      .addCase(deleteSiteById.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteSiteById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = null;

        // Update the site status to DELETED in the list
        if (state.site?.sites) {
          const siteIndex = state.site.sites.findIndex(
            (site) => site.id === action.payload.siteId,
          );
          if (siteIndex !== -1) {
            state.site.sites[siteIndex].status = "DELETED";
            state.site.sites[siteIndex].metadata = {
              ...state.site.sites[siteIndex].metadata,
              softDeletedAt: new Date().toISOString(),
              canRestoreUntil: action.payload.data?.canRestoreUntil,
            };
          }
        }

        // Update single site if it's the one being deleted
        if (state.singleSite?.site?.id === action.payload.siteId) {
          state.singleSite.site.status = "DELETED";
          state.singleSite.site.metadata = {
            ...state.singleSite.site.metadata,
            softDeletedAt: new Date().toISOString(),
            canRestoreUntil: action.payload.data?.canRestoreUntil,
          };
        }
      })
      .addCase(deleteSiteById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || action.error.message;
      })

      // Restore site
      .addCase(restoreSite.pending, (state) => {
        state.restoreLoading = true;
        state.restoreError = null;
      })
      .addCase(restoreSite.fulfilled, (state, action) => {
        state.restoreLoading = false;
        state.restoreError = null;

        // Update the site status in the list
        if (state.site?.sites) {
          const siteIndex = state.site.sites.findIndex(
            (site) => site.id === action.payload.siteId,
          );
          if (siteIndex !== -1) {
            state.site.sites[siteIndex].status =
              action.payload.data?.status || "ACTIVE";
            // Remove soft delete metadata
            if (state.site.sites[siteIndex].metadata) {
              delete state.site.sites[siteIndex].metadata.softDeletedAt;
              delete state.site.sites[siteIndex].metadata.canRestoreUntil;
              delete state.site.sites[siteIndex].metadata.scheduledDeletionAt;
            }
          }
        }

        // Update single site
        if (state.singleSite?.site?.id === action.payload.siteId) {
          state.singleSite.site.status =
            action.payload.data?.status || "ACTIVE";
          if (state.singleSite.site.metadata) {
            delete state.singleSite.site.metadata.softDeletedAt;
            delete state.singleSite.site.metadata.canRestoreUntil;
            delete state.singleSite.site.metadata.scheduledDeletionAt;
          }
        }
      })
      .addCase(restoreSite.rejected, (state, action) => {
        state.restoreLoading = false;
        state.restoreError = action.payload || action.error.message;
      })

      // Permanently delete site
      .addCase(permanentlyDeleteSiteById.pending, (state) => {
        state.permanentDeleteLoading = true;
        state.permanentDeleteError = null;
      })
      .addCase(permanentlyDeleteSiteById.fulfilled, (state, action) => {
        state.permanentDeleteLoading = false;
        state.permanentDeleteError = null;

        // Remove the site from the list
        if (state.site?.sites) {
          state.site.sites = state.site.sites.filter(
            (site) => site.id !== action.payload.siteId,
          );
          // Update total count
          if (state.site.pagination) {
            state.site.pagination.totalCount -= 1;
          }
        }

        // Clear single site if it's the one being deleted
        if (state.singleSite?.site?.id === action.payload.siteId) {
          state.singleSite = null;
        }
      })
      .addCase(permanentlyDeleteSiteById.rejected, (state, action) => {
        state.permanentDeleteLoading = false;
        state.permanentDeleteError = action.payload || action.error.message;
      })

      // Clone site
      .addCase(cloneSite.pending, (state) => {
        state.cloneLoading = true;
        state.cloneError = null;
      })
      .addCase(cloneSite.fulfilled, (state, action) => {
        state.cloneLoading = false;
        // Optionally add the new cloned site to the list
      })
      .addCase(cloneSite.rejected, (state, action) => {
        state.cloneLoading = false;
        state.cloneError = action.payload || action.error.message;
      })

      // Migrate site
      .addCase(migrateSite.pending, (state) => {
        state.migrateLoading = true;
        state.migrateError = null;
      })
      .addCase(migrateSite.fulfilled, (state, action) => {
        state.migrateLoading = false;
      })
      .addCase(migrateSite.rejected, (state, action) => {
        state.migrateLoading = false;
        state.migrateError = action.payload || action.error.message;
      })

      // Download complete site
      .addCase(downloadCompleteSite.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadCompleteSite.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadCompleteSite.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload || action.error.message;
      })

      // Migrate environment
      .addCase(migrateEnvironment.pending, (state) => {
        state.migrateEnvironmentLoading = true;
        state.migrateEnvironmentError = null;
      })
      .addCase(migrateEnvironment.fulfilled, (state) => {
        state.migrateEnvironmentLoading = false;
      })
      .addCase(migrateEnvironment.rejected, (state, action) => {
        state.migrateEnvironmentLoading = false;
        state.migrateEnvironmentError = action.payload || action.error.message;
      })

      // PHPMyAdmin login
      .addCase(getPhpMyAdminLogin.pending, (state) => {
        state.quickAccessLoading = true;
        state.quickAccessError = null;
      })
      .addCase(getPhpMyAdminLogin.fulfilled, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccess = action.payload;
      })
      .addCase(getPhpMyAdminLogin.rejected, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccessError = action.payload || action.error.message;
      })

      // WP Admin login
      .addCase(getWpAdminLogin.pending, (state) => {
        state.quickAccessLoading = true;
        state.quickAccessError = null;
      })
      .addCase(getWpAdminLogin.fulfilled, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccess = action.payload;
      })
      .addCase(getWpAdminLogin.rejected, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccessError = action.payload || action.error.message;
      })

      // Quick access links
      .addCase(getQuickAccessLinks.pending, (state) => {
        state.quickAccessLoading = true;
        state.quickAccessError = null;
      })
      .addCase(getQuickAccessLinks.fulfilled, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccess = action.payload;
      })
      .addCase(getQuickAccessLinks.rejected, (state, action) => {
        state.quickAccessLoading = false;
        state.quickAccessError = action.payload || action.error.message;
      })

      // Site metrics (storage, CPU, memory, performance)
      .addCase(fetchSiteMetrics.pending, (state) => {
        state.metricsLoading = true;
        state.metricsError = null;
      })
      .addCase(fetchSiteMetrics.fulfilled, (state, action) => {
        state.metricsLoading = false;
        // API returns {success, data}, extract the data
        state.siteMetrics = action.payload?.data
          ? action.payload
          : { data: action.payload };
      })
      .addCase(fetchSiteMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        state.metricsError = action.payload || action.error.message;
      });
  },
});

export const {
  clearSingleSite,
  clearDeleteError,
  clearRestoreError,
  clearErrors,
  clearQuickAccess,
  clearSiteMetrics,
} = siteSlice.actions;
export default siteSlice.reducer;
